/* global BackboneEx */
/* eslint func-names: 0 */
import _ from 'common/underscore';
import { trackEvent } from 'common/analytics';
import App from 'common/backbone-app';
import React from 'react';
import ReactDOM from 'react-dom';
import ReactDialog from 'components/common/react-dialog';
import { Button } from 'components/common/Button';
import { Notice } from 'components/common/Notice';
import { DialogFooter } from 'components/common/DialogFooter';
import Loader from 'components/common/Loader';

const POLLING_COUNTRY_CODES = ['ES', 'AT'];
const POLLING_RETRY_CODES = [400, 404, 409];
const POLLING_RATE = 2000; // 2 seconds between polls
const MAX_POLLING_ATTEMPTS = window.Cypress ? 2 : 15; // 15 attempts = 30 seconds total

/**
 * POS transaction complete view.
 */
App.Views.Forms.POSTransactionComplete = BackboneEx.View.Dialog2.extend({
  events: {
    'click .js-close': 'close',
    'click .js-open-register': 'doOpenRegister',
    'click .js-add-tip': 'doAddTip',
    'click .js-rebook': 'rebook',
    'click .js-download-original-receipt': 'onGetOriginalReceiptClick',
    'click .js-receipt-copy-reason': 'onReceiptCopyClick',
  },

  templateId: 'pos-transaction-complete-template',

  options: {
    width: 280,
    dialogClass: 'pos-complete',
    dataTest: 'pos-transaction-complete-modal',
  },
  dialogTitle: Wahanda.lang.calendar.appointments.pos.completeTitle,

  originalReceiptDownloaded: false,
  pollingError: null,
  pollingAttemptCount: 0,
  pollingVenueId: null,
  pollingReceiptUri: null,
  errorModalContainer: null,
  loadingModalContainer: null,
  isPolling: false,

  initialize(...props) {
    BackboneEx.View.Dialog2.prototype.initialize.apply(this, props);
    this.collection = this.options.collection;
    this.controller = this.options.controller;
    this.checkoutCalc = this.controller ? this.controller.checkoutCalc : null;
    this.customTotalLabel = this.options.customTotalLabel;
    this.customTotal = this.options.customTotal;
  },

  render(...props) {
    BackboneEx.View.Dialog2.prototype.render.apply(this, props);
    this.hydrate();
  },

  hydrate() {
    this.$dialogContent
      .empty()
      .prepend(Wahanda.Template.renderTemplate(this.templateId, this.getDialogTemplateVariables()));
  },

  doOpenRegister() {
    // TODO
  },

  maybeGetEventPosition(event) {
    return event ? { x: event.clientX, y: event.clientY } : null;
  },

  doAddTip() {
    // TODO
  },

  onGetOriginalReceiptClick(e) {
    e.preventDefault();

    const countryCode = App.config.get('venue')?.countryCode;
    const shouldPoll = POLLING_COUNTRY_CODES.includes(countryCode);

    const venueId = App.getVenueId() || App.config.get('venue')?.id;
    const receiptUri = this.options.receiptUri;

    const missingLink = !e.target.href;
    const missingPollingValues = shouldPoll && (!venueId || !receiptUri);

    if (missingLink || missingPollingValues) {
      console.error('Could not obtain receipt');
      return false;
    }

    if (shouldPoll) {
      this.pollForReceipt(venueId.toString(), receiptUri);
    } else {
      // open directly
      window.open(e.target.href, '_blank');
    }

    if (this.options.receiptCopyUri) {
      this.originalReceiptDownloaded = true;
    }
    this.hydrate();
    return true;
  },

  showPollingLoadingModal() {
    // Clean up any existing container first to ensure fresh render
    if (this.loadingModalContainer) {
      try {
        ReactDOM.unmountComponentAtNode(this.loadingModalContainer);
      } catch (e) {
        // Ignore errors if already unmounted
      }
      if (this.loadingModalContainer.parentNode) {
        this.loadingModalContainer.parentNode.removeChild(this.loadingModalContainer);
      }
      this.loadingModalContainer = null;
    }

    // Create fresh container
    this.loadingModalContainer = document.createElement('div');
    this.loadingModalContainer.id = 'pos-transaction-polling-loading';
    document.body.appendChild(this.loadingModalContainer);

    ReactDOM.render(
      <div data-test="polling-loading-modal" className="checkout-summary loading-modal">
        <Loader />
      </div>,
      this.loadingModalContainer,
    );
  },

  hidePollingLoadingModal() {
    if (this.loadingModalContainer) {
      ReactDOM.unmountComponentAtNode(this.loadingModalContainer);
      if (this.loadingModalContainer.parentNode) {
        this.loadingModalContainer.parentNode.removeChild(this.loadingModalContainer);
      }
      this.loadingModalContainer = null;
    }
  },

  getPollingErrorMessage() {
    if (!this.pollingError) return '';
    const status = this.pollingError.status;
    switch (status) {
      case 400:
        return Wahanda.lang.pos.printReceipt.invalidRequest;
      case 404:
        return Wahanda.lang.pos.printReceipt.receiptNotFound;
      case 409:
        return Wahanda.lang.pos.printReceipt.receiptBeingGenerated;
      default:
        return Wahanda.lang.pos.printReceipt.receiptErrorServer;
    }
  },

  showPollingErrorModal() {
    // Create container if it doesn't exist
    if (!this.errorModalContainer) {
      this.errorModalContainer = document.createElement('div');
      this.errorModalContainer.id = 'pos-transaction-polling-error';
      document.body.appendChild(this.errorModalContainer);
    }

    const self = this;
    const handleRetry = () => {
      self.hidePollingErrorModal();
      if (self.pollingVenueId && self.pollingReceiptUri) {
        self.pollForReceipt(self.pollingVenueId, self.pollingReceiptUri);
      }
    };

    const handleClose = () => {
      self.hidePollingErrorModal();
    };

    const footerContent = (
      <>
        <div className="checkout-summary footer-content">
          <Notice type="alert" message={this.getPollingErrorMessage()} />
        </div>
        <DialogFooter fullWidth>
          <div className="button-wrapper">
            <Button label="Close" colour="plain" variant="secondary" onClick={handleClose} />
          </div>
          <div className="button-wrapper">
            <Button
              label="Retry"
              onClick={handleRetry}
              size="medium"
              dataTest="receipt-polling-retry"
            />
          </div>
        </DialogFooter>
      </>
    );

    ReactDOM.render(
      <ReactDialog
        dataTest="receipt-polling-error-modal"
        title={Wahanda.lang.pos.printReceipt.errorLoadingReceipt}
        classes={{ 'checkout-summary': true }}
        width={470}
        onClose={handleClose}
      >
        {footerContent}
      </ReactDialog>,
      this.errorModalContainer,
    );
  },

  hidePollingErrorModal() {
    if (this.errorModalContainer) {
      ReactDOM.unmountComponentAtNode(this.errorModalContainer);
      if (this.errorModalContainer.parentNode) {
        this.errorModalContainer.parentNode.removeChild(this.errorModalContainer);
      }
      this.errorModalContainer = null;
    }
  },

  pollForReceipt(venueId, receiptUri) {
    // Reset polling state
    this.pollingError = null;
    this.pollingAttemptCount = 0;
    this.pollingVenueId = venueId;
    this.pollingReceiptUri = receiptUri;
    this.isPolling = true;

    this.showPollingLoadingModal();

    const receiptUrl = `/api/point-of-sale/venue/${venueId}/receipt/${receiptUri}`;
    const self = this;

    const poll = () => {
      self.pollingAttemptCount += 1;

      const xhr = new XMLHttpRequest();
      xhr.open('GET', receiptUrl, true);
      xhr.setRequestHeader('Accept', 'application/pdf');

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          // Success:
          self.pollingError = null;
          self.isPolling = false;
          self.hidePollingLoadingModal();
          window.open(receiptUrl, '_blank');
        } else {
          // Error
          const status = xhr.status;
          const isRetryable = POLLING_RETRY_CODES.includes(status);
          const isMaxAttemptsReached = self.pollingAttemptCount >= MAX_POLLING_ATTEMPTS;

          if (isMaxAttemptsReached) {
            // stop polling and show error
            self.isPolling = false;
            const error = new Error(`Failed to fetch receipt: ${status}`);
            error.status = status;
            self.pollingError = error;

            self.hidePollingLoadingModal();
            self.showPollingErrorModal();
            return;
          }

          if (!isRetryable) {
            // Non-retryable error - stop polling and show error
            self.isPolling = false;
            const error = new Error(`Failed to fetch receipt: ${status}`);
            error.status = status;
            self.pollingError = error;

            self.hidePollingLoadingModal();
            self.showPollingErrorModal();
            return;
          }

          setTimeout(poll, POLLING_RATE);
        }
      };

      xhr.onerror = () => {
        const isMaxAttemptsReached = self.pollingAttemptCount >= MAX_POLLING_ATTEMPTS;
        if (isMaxAttemptsReached) {
          self.isPolling = false;
          const error = new Error('Failed to fetch receipt: network error');
          self.pollingError = error;

          self.hidePollingLoadingModal();
          self.showPollingErrorModal();
          return;
        }
        setTimeout(poll, POLLING_RATE);
      };

      xhr.send();
    };

    // Start polling
    poll();
  },

  onReceiptCopyClick() {
    App.ES6.Initializers.ReceiptCopyDialog({
      node: this.$('.js-receipt-copy-dialog')[0],
    }).render(this.options);

    return true;
  },

  remove() {
    // Clean up loading modal if it exists
    this.hidePollingLoadingModal();
    if (this.loadingModalContainer) {
      if (this.loadingModalContainer.parentNode) {
        this.loadingModalContainer.parentNode.removeChild(this.loadingModalContainer);
      }
      this.loadingModalContainer = null;
    }

    // Clean up error modal if it exists
    if (this.errorModalContainer) {
      ReactDOM.unmountComponentAtNode(this.errorModalContainer);
      if (this.errorModalContainer.parentNode) {
        this.errorModalContainer.parentNode.removeChild(this.errorModalContainer);
      }
      this.errorModalContainer = null;
    }

    // Call parent remove
    BackboneEx.View.Dialog2.prototype.remove.call(this);
  },

  rebook(event) {
    trackEvent('calendar', 'click', 'rebook-init', 'pos-transaction-complete');
    this.close();
    Wahanda.Appointment.enterRebookingMode(this.collection, this.maybeGetEventPosition(event));
  },

  getDialogTemplateVariables() {
    const date = this.formatDate(Wahanda.Date.createVenueDate());
    const receiptPdfUri = this.options.receiptPdfUri;
    const receiptButtonLabel = this.options.receiptButtonLabel
      ? this.options.receiptButtonLabel
      : Wahanda.lang.calendar.appointments.pos.completeRegister;
    const receiptCopyUri = this.options.receiptCopyUri;
    const secondReceiptUri = this.options.secondReceiptUri;
    const secondReceiptButtonLabel = this.options.secondReceiptButtonLabel;
    const defaultVariables = {
      date,
      receiptPdfUri,
      receiptButtonLabel,
      receiptCopyUri,
      receiptCopyButtonLabel: this.options.receiptCopyButtonLabel,
      secondReceiptUri,
      secondReceiptButtonLabel,
      originalReceiptDownloaded: this.originalReceiptDownloaded,
    };
    let additionalVariables = {};
    if (this.customTotal != null) {
      additionalVariables = this.getCustomTotalTemplateVariables();
    } else if (this.collection) {
      additionalVariables = this.getCheckoutTemplateVariables();
    }

    return {
      ...defaultVariables,
      ...additionalVariables,
    };
  },

  getCustomTotalTemplateVariables() {
    const { customTotal, customTotalLabel } = this;

    return {
      hasReceipt: true,
      customTotal: this.formatNumber(customTotal.toFixed(2)),
      customTotalLabel,
    };
  },

  getCheckoutTemplateVariables() {
    const product = App.Models.POS.ProductDescriminators.PRODUCT;
    const voucher = App.Models.POS.ProductDescriminators.VOUCHER;

    const changeAmount = this.checkoutCalc.getChange().toFixed(2);
    const servicesTotal = this.checkoutCalc.getServicesTotal().toFixed(2);
    const totalPaid = this.controller.payments.getTotalPaidSum();

    const productsTotal = this.controller.products.getTotalDiscounted(product).toFixed(2);
    const productsQuantity = this.controller.products.getTotalQuantity(product);
    const productsQuantityText = this.getProductText(product, productsQuantity);

    const vouchersTotal = this.controller.products.getTotalDiscounted(voucher).toFixed(2);
    const vouchersQuantity = this.controller.products.getTotalQuantity(voucher);
    const vouchersQuantityText = this.getProductText(voucher, vouchersQuantity);

    const tipsTotal = this.checkoutCalc.tips.getTotal();
    const totalDeductions = this.checkoutCalc.getDeductions().toFixed(2);

    const totalPayments = (totalPaid + tipsTotal).toFixed(2);
    const isPaidWithVATVoucher = this.controller.payments.getTotalVouchersWithVAT() > 0;

    return {
      hasReceipt: true,
      hasServices: this.controller.getServiceLineItems().length > 0,
      hasAppts: !_.isEmpty(this.collection),
      servicesTotal: this.formatNumber(servicesTotal),
      hasProducts: productsQuantity > 0,
      productsTotal: this.formatNumber(productsTotal),
      productsQuantityText,
      hasVouchers: vouchersQuantity > 0,
      vouchersTotal: this.formatNumber(vouchersTotal),
      vouchersQuantityText,
      hasTips: tipsTotal > 0,
      tipsTotal: this.formatNumber(tipsTotal.toFixed(2)),
      showAddTip: false,
      hasDeductions: totalDeductions > 0,
      totalDeductions: this.formatNumber(totalDeductions),
      totalPaid: this.formatNumber(totalPayments),
      hasChange: changeAmount > 0,
      change: this.formatNumber(changeAmount),
      isPaidWithVATVoucher,
      paidWithVATVoucher: this.formatNumber(this.controller.payments.getTotalVouchersWithVAT()),
    };
  },

  getProductText(type, quantity) {
    const single =
      type === App.Models.POS.ProductDescriminators.VOUCHER
        ? Wahanda.lang.calendar.appointments.pos.completeSingleVoucher
        : Wahanda.lang.calendar.appointments.pos.completeSingleProduct;
    const multiple =
      type === App.Models.POS.ProductDescriminators.VOUCHER
        ? Wahanda.lang.calendar.appointments.pos.completeMultipleVouchers
        : Wahanda.lang.calendar.appointments.pos.completeMultipleProducts;
    if (quantity > 1) {
      return `${quantity} ${multiple}`;
    }
    return `${quantity} ${single}`;
  },

  formatNumber(num) {
    return Wahanda.Currency.getFormatted(num);
  },

  formatDate(date) {
    const format = 'M dd yy';
    return Wahanda.Date.formatDate(format, date);
  },
});
